import { dungApiThat, goiApi } from '../api/http';
import {
  hanMucTokenCua,
  hanMucTokenKhongGioiHan,
  mayChuHanMucToken,
} from '../../mock/han-muc-token';
import { mayChuCapThemToken } from '../../mock/cap-them-token';
import { mayChuTenant } from '../../mock/tenant';
import { mockRequest, mockState } from '../../mock/transport';
import type {
  AiTokenPriority,
  AiTokenQuota,
  AiTokenQuotaUpdate,
  PlanAiTokenQuota,
  PlanCode,
} from '../../modules/tenant/types';

/**
 * Hạn mức token AI theo tháng (ADR-0088, tenant-service, tag `ai-quota` của `openapi/platform-api.yaml`).
 *
 * Mỗi hàm chọn **lúc gọi** giữa API thật và máy chủ giả (`mock/han-muc-token.ts`), nên `queryKey` và việc
 * vô hiệu hoá cache hành xử y hệt ở cả hai chế độ. `?mock=empty` diễn trạng thái **không giới hạn** — hợp
 * đồng không có "rỗng" nào khác.
 *
 * - Đọc hạn mức một tenant: `PLATFORM_ADMIN`, hoặc `TENANT_ADMIN` của chính tenant đó (`tenant_id` lấy từ
 *   ngữ cảnh đăng nhập, không bao giờ từ ô nhập).
 * - Đặt ghi đè và mọi thứ theo gói: chỉ `PLATFORM_ADMIN`.
 */
const theoTenant = (id: string) => `/api/v1/tenants/${encodeURIComponent(id)}/ai-token-quota`;
const GOC_GOI = '/api/v1/security/ai-token-quota';

/** Máy chủ giả chạy **sau** nhịp chờ và các trạng thái `?mock=`: lỗi tới cùng độ trễ với lỗi thật. */
const giaLap = <T>(lam: () => T): Promise<T> => mockRequest(undefined).then(lam);

export const hanMucTokenRepository = {
  /** `GET /api/v1/tenants/{id}/ai-token-quota` — hạn mức và số đã dùng của tháng hiện tại. */
  cuaTenant: (tenantId: string): Promise<AiTokenQuota> =>
    dungApiThat()
      ? goiApi<AiTokenQuota>(theoTenant(tenantId))
      : giaLap(() => {
          const t = mayChuTenant.chiTiet(tenantId);
          return mockState() === 'empty'
            ? hanMucTokenKhongGioiHan(tenantId, t.plan_code)
            : hanMucTokenCua(tenantId, t.plan_code, t.resource_package_code);
        }),

  /** `PUT /api/v1/tenants/{id}/ai-token-quota` — `limit_override: null` là bỏ ghi đè, về hạn mức của gói. */
  datGhiDe: (tenantId: string, du: AiTokenQuotaUpdate): Promise<AiTokenQuota> =>
    dungApiThat()
      ? goiApi<AiTokenQuota>(theoTenant(tenantId), { method: 'PUT', body: du })
      : giaLap(() =>
          mayChuHanMucToken.datGhiDe(
            tenantId,
            mayChuTenant.chiTiet(tenantId).plan_code,
            mayChuTenant.chiTiet(tenantId).resource_package_code,
            du.limit_override,
          ),
        ),

  /**
   * `PUT /api/v1/tenants/{id}/ai-token-quota/priority` — thứ tự trừ token (`ai_quota.uu_tien`, ADR-0091 mục 4).
   * Platform Admin (`TENANT.UPDATE`) hoặc Tenant Admin của chính tenant; trả hạn mức mới.
   */
  datUuTien: (tenantId: string, priority: AiTokenPriority): Promise<AiTokenQuota> =>
    dungApiThat()
      ? goiApi<AiTokenQuota>(`${theoTenant(tenantId)}/priority`, { method: 'PUT', body: { priority } })
      : giaLap(() => {
          mayChuCapThemToken.datUuTien(tenantId, priority);
          const t = mayChuTenant.chiTiet(tenantId);
          return hanMucTokenCua(tenantId, t.plan_code, t.resource_package_code);
        }),

  /** `GET /api/v1/security/ai-token-quota` — đúng bốn gói, theo thứ tự enum. */
  theoGoi: (): Promise<PlanAiTokenQuota[]> =>
    dungApiThat()
      ? goiApi<{ items: PlanAiTokenQuota[] }>(GOC_GOI).then((t) => t.items)
      : giaLap(() => (mockState() === 'empty' ? mayChuHanMucToken.theoGoiRong() : mayChuHanMucToken.theoGoi()).items),

  /** `PUT /api/v1/security/ai-token-quota/{plan_code}` — áp ngay cho mọi tenant của gói không có ghi đè. */
  datTheoGoi: (goi: PlanCode, limitValue: number): Promise<PlanAiTokenQuota> =>
    dungApiThat()
      ? goiApi<PlanAiTokenQuota>(`${GOC_GOI}/${encodeURIComponent(goi)}`, {
          method: 'PUT',
          body: { limit_value: limitValue },
        })
      : giaLap(() => mayChuHanMucToken.datTheoGoi(goi, limitValue)),
};
